import React, { useEffect, useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import notifyHB from '@/config/notifyHB';
import { RecurrenteProduct } from '@/entities/payment';
import { getPaymentProducts } from '@/use-cases/get-payment-products';
import styles from './personal-info.module.scss';

interface PaymentProductPickerProps {
	onSelect: (productId: string) => void;
}

/**
 * Plan selection for a payment step resumed without a product (the form that
 * picks it ran in another session). A single plan is picked without asking.
 */
const PaymentProductPicker: React.FC<PaymentProductPickerProps> = ({
	onSelect,
}) => {
	const [products, setProducts] = useState<RecurrenteProduct[]>([]);
	const [productsStatus, setProductsStatus] = useState<
		'loading' | 'ready' | 'error'
	>('loading');
	const [selectedProductId, setSelectedProductId] = useState('');

	const loadProducts = () => {
		setProductsStatus('loading');
		getPaymentProducts()
			.then((loadedProducts) => {
				if (loadedProducts.length === 1) {
					onSelect(loadedProducts[0].id);
					return;
				}
				setProducts(loadedProducts);
				setProductsStatus('ready');
			})
			.catch((err) => {
				setProductsStatus('error');
				notifyHB('PaymentProductPicker', 'getPaymentProducts', err);
			});
	};

	useEffect(() => {
		loadProducts();
	}, []);

	if (productsStatus === 'loading') {
		return (
			<p className={styles['products-message']}>
				<Loader2Icon className="animate-spin" size={16} />
				Cargando productos...
			</p>
		);
	}

	if (productsStatus === 'error') {
		return (
			<div className={styles['products-message']}>
				<span>No pudimos cargar los productos disponibles.</span>
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={loadProducts}
				>
					Reintentar
				</Button>
			</div>
		);
	}

	if (products.length === 0) {
		return (
			<p className={styles['products-message']}>
				No hay productos disponibles en este momento. Escríbenos a{' '}
				<a href="mailto:contacto@cybersign.gt">contacto@cybersign.gt</a>
				.
			</p>
		);
	}

	return (
		<>
			<span className={styles['products-legend']}>
				Selecciona tu plan
			</span>
			<RadioGroup
				value={selectedProductId}
				onValueChange={setSelectedProductId}
				className={styles['product-selector']}
			>
				{products.map((product) => (
					<Label
						key={product.id}
						htmlFor={product.id}
						className={styles['product-option']}
					>
						<RadioGroupItem
							value={product.id}
							id={product.id}
							className={styles['product-radio']}
						/>
						<span className={styles['product-label']}>
							<span className={styles['product-name']}>
								{product.name}
							</span>
							<span className={styles['product-description']}>
								{product.description}
							</span>
							<span className={styles['product-price']}>
								{product.currency} {product.price.toFixed(2)}
							</span>
						</span>
					</Label>
				))}
			</RadioGroup>
			<Button
				disabled={!selectedProductId}
				onClick={() => onSelect(selectedProductId)}
			>
				Continuar al pago
			</Button>
		</>
	);
};

export default PaymentProductPicker;
